import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Card,
  Space,
  Row,
  Col,
  Upload,
  Image
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { albumAPI, photoAPI, IMG_URL } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import { validateTextField, validateDescription, containsSqlInjection } from '../utils/validator';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useParams } from 'react-router-dom';

const AlbumManagement = () => {
  const { user } = useAuth();
  const { username } = useParams();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [form] = Form.useForm();
  const [albumPhotos, setAlbumPhotos] = useState([]); // 相册照片列表
  const [coverImageModalVisible, setCoverImageModalVisible] = useState(false); // 封面选择模态框

  // 根据是新建还是编辑动态设置网页标题
  const pageTitle = editingAlbum ? '编辑相册' : '相册管理';
  useDocumentTitle(pageTitle, {
    prefix: '',
    suffix: ' - rtycn'
  });

  // 从URL参数读取相册ID：/album/edit?id=x 场景自动打开对应相册的编辑框
  const queryParams = new URLSearchParams(window.location.search);
  const editId = queryParams.get('id');
  const isEditPage = window.location.pathname.includes('/album/edit/');

  useEffect(() => {
    fetchAlbums();
  }, []);

  // edit 场景：相册列表加载完成后自动打开对应相册的编辑框
  useEffect(() => {
    if (isEditPage && editId && albums.length > 0) {
      const target = albums.find(a => String(a.id) === String(editId));
      if (target) {
        handleEdit(target);
      } else {
        message.warning('未找到该相册，可能已被删除');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albums, isEditPage, editId]);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      const response = await albumAPI.getAlbumsByAuthor(user?.username || '');
      setAlbums(response.data || []);
    } catch (error) {
      message.error('获取相册列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAlbum(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingAlbum(record);
    form.setFieldsValue(record);
    // 加载相册的照片列表
    try {
      const response = await photoAPI.getPhotosByAlbum(record.id);
      setAlbumPhotos(response.data || []);
    } catch (error) {
      console.error('获取相册照片失败:', error);
      setAlbumPhotos([]);
    }
    setModalVisible(true);
  };

  // 打开封面图片选择模态框
  const handleOpenCoverImageModal = () => {
    setCoverImageModalVisible(true);
  };

  // 选择封面图片
  const handleSelectCoverImage = (photo) => {
    form.setFieldsValue({ coverImage: photo.imageUrl });
    setCoverImageModalVisible(false);
  };

  const handleDelete = async (albumId) => {
    try {
      await albumAPI.deleteAlbum(albumId);
      message.success('删除成功');
      fetchAlbums();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 前端验证
      if (values.title) {
        const titleResult = validateTextField(values.title, { required: true, fieldName: '相册名称', max: 200 });
        if (!titleResult.valid) {
          message.error(titleResult.message);
          return;
        }
      }

      if (values.description) {
        const descResult = validateDescription(values.description, { fieldName: '相册描述', max: 500 });
        if (!descResult.valid) {
          message.error(descResult.message);
          return;
        }
      }

      if (values.coverImage) {
        if (containsSqlInjection(values.coverImage)) {
          message.error('封面图片URL包含非法字符');
          return;
        }
      }

      const submitData = {
        ...values,
        authorId: user?.username,
        status: 1
      };

      if (editingAlbum) {
        await albumAPI.updateAlbum(editingAlbum.id, submitData);
        message.success('更新成功');
      } else {
        await albumAPI.createAlbum(submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchAlbums();
    } catch (error) {
      message.error(editingAlbum ? '更新失败' : '创建失败');
    }
  };

  const columns = [
    {
      title: '相册封面',
      dataIndex: 'coverImage',
      key: 'coverImage',
      width: 100,
      render: (coverImage) => (
        coverImage ? (
          <Image
            width={60}
            height={60}
            src={`${IMG_URL}${coverImage}`}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{
              mask: <EyeOutlined />,
            }}
          />
        ) : (
          <div style={{
            width: 60,
            height: 60,
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4
          }}>
            无封面
          </div>
        )
      ),
    },
    {
      title: '相册名称',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '照片数量',
      dataIndex: 'photoCount',
      key: 'photoCount',
      render: (count) => count || 0,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个相册吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
      <Card
        title="相册管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建相册
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={albums}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个相册`,
          }}
        />
      </Card>

      <Modal
        title={editingAlbum ? '编辑相册' : '新建相册'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAlbum(null);
          setAlbumPhotos([]);
        }}
        footer={null}
        width={600}
      >

      {/* 封面图片选择模态框 */}
      <Modal
        title="选择封面图片"
        open={coverImageModalVisible}
        onCancel={() => setCoverImageModalVisible(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        {albumPhotos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            该相册暂无照片
          </div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              {albumPhotos.map((photo) => (
                <Col key={photo.id} xs={8} sm={6} md={4} lg={4}>
                  <Card
                    hoverable
                    cover={
                      <Image
                        alt={photo.title}
                        src={photo.imageUrl ? `${IMG_URL}${photo.imageUrl}` : '/default-photo.jpg'}
                        width={'100%'}
                        height={120}
                        style={{ objectFit: 'cover', display: 'block' }}
                        preview={false}
                      />
                    }
                    onClick={() => handleSelectCoverImage(photo)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Meta
                      title={<div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.title}</div>}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="相册名称"
            rules={[
              { required: true, message: '请输入相册名称' },
              { max: 200, message: '相册名称不能超过200个字符' }
            ]}
          >
            <Input placeholder="请输入相册名称" maxLength={200} />
          </Form.Item>

          <Form.Item
            name="description"
            label="相册描述"
          >
            <Input.TextArea
              placeholder="请输入相册描述"
              rows={4}
            />
          </Form.Item>

          <Form.Item
            name="coverImage"
            label="封面图片URL"
          >
            <Input
              placeholder="请输入封面图片URL"
              addonAfter={
                <Button
                  type="text"
                  icon={<UploadOutlined />}
                  onClick={handleOpenCoverImageModal}
                  disabled={!editingAlbum || albumPhotos.length === 0}
                >
                  选择相册图片
                </Button>
              }
            />
          </Form.Item>

          <Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Button onClick={() => setModalVisible(false)}>
                  取消
                </Button>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit">
                  {editingAlbum ? '更新' : '创建'}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AlbumManagement;